/**
 * Keccak-256 (the pre-NIST padding Ethereum uses — **not** SHA3-256), synchronous and dependency-free.
 *
 * It exists for the EIP-55 checksum: a mixed-case EVM address encodes a checksum in its capitalisation, and
 * verifying it offline rejects a mistyped address before an irreversible transfer is created. `SubtleCrypto`
 * offers neither Keccak nor a synchronous API, and the address validator runs inside a Zod `refine`.
 *
 * Implemented over `BigInt` lanes rather than 32-bit halves: it is a few microseconds slower per hash — and
 * this hashes a 40-character string on a debounced input, not a stream — in exchange for being short enough
 * to read against the spec. Verified against the standard vectors in the sibling test file.
 */

const MASK64 = (1n << 64n) - 1n;

/** Keccak-f[1600] round constants, one per round. */
const ROUND_CONSTANTS: readonly bigint[] = [
  0x0000000000000001n,
  0x0000000000008082n,
  0x800000000000808an,
  0x8000000080008000n,
  0x000000000000808bn,
  0x0000000080000001n,
  0x8000000080008081n,
  0x8000000000008009n,
  0x000000000000008an,
  0x0000000000000088n,
  0x0000000080008009n,
  0x000000008000000an,
  0x000000008000808bn,
  0x800000000000008bn,
  0x8000000000008089n,
  0x8000000000008003n,
  0x8000000000008002n,
  0x8000000000000080n,
  0x000000000000800an,
  0x800000008000000an,
  0x8000000080008081n,
  0x8000000000008080n,
  0x0000000080000001n,
  0x8000000080008008n,
];

/** Rotation offsets ρ, flattened as `x + 5y`. */
const ROTATION: readonly number[] = [
  0, 1, 62, 28, 27, 36, 44, 6, 55, 20, 3, 10, 43, 25, 39, 41, 45, 15, 21, 8, 18, 2, 61, 56, 14,
];

/** Rate for Keccak-256: 1600 − 2×256 bits = 136 bytes. */
const RATE_BYTES = 136;

const rotl64 = (value: bigint, bits: number): bigint => {
  const n = BigInt(bits);
  return ((value << n) | (value >> (64n - n))) & MASK64;
};

function keccakF1600(state: bigint[]): void {
  const c = new Array<bigint>(5);
  const d = new Array<bigint>(5);
  const b = new Array<bigint>(25);

  for (let round = 0; round < 24; round++) {
    // θ — parity of each column, folded back into every lane of the neighbouring columns.
    for (let x = 0; x < 5; x++) {
      c[x] = state[x] ^ state[x + 5] ^ state[x + 10] ^ state[x + 15] ^ state[x + 20];
    }
    for (let x = 0; x < 5; x++) {
      d[x] = c[(x + 4) % 5] ^ rotl64(c[(x + 1) % 5], 1);
    }
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 5; y++) state[x + 5 * y] ^= d[x];
    }

    // ρ (rotate each lane) and π (permute their positions) in one pass.
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 5; y++) {
        b[y + 5 * ((2 * x + 3 * y) % 5)] = rotl64(state[x + 5 * y], ROTATION[x + 5 * y]);
      }
    }

    // χ — the only non-linear step.
    for (let x = 0; x < 5; x++) {
      for (let y = 0; y < 5; y++) {
        state[x + 5 * y] = b[x + 5 * y] ^ (~b[((x + 1) % 5) + 5 * y] & b[((x + 2) % 5) + 5 * y] & MASK64);
      }
    }

    // ι — break the symmetry between rounds.
    state[0] ^= ROUND_CONSTANTS[round];
  }
}

export function keccak256(message: Uint8Array): Uint8Array {
  const state = new Array<bigint>(25).fill(0n);

  // Keccak padding is `0x01 … 0x80` (pad10*1). SHA3 uses `0x06` instead, which is the single byte that
  // separates the two functions — getting it wrong yields a plausible-looking but wrong digest.
  const padded = new Uint8Array(Math.ceil((message.length + 1) / RATE_BYTES) * RATE_BYTES);
  padded.set(message);
  padded[message.length] = 0x01;
  padded[padded.length - 1] |= 0x80;

  for (let offset = 0; offset < padded.length; offset += RATE_BYTES) {
    for (let i = 0; i < RATE_BYTES / 8; i++) {
      let lane = 0n;
      // Little-endian, as Keccak specifies.
      for (let byte = 7; byte >= 0; byte--) {
        lane = (lane << 8n) | BigInt(padded[offset + i * 8 + byte]);
      }
      state[i] ^= lane;
    }
    keccakF1600(state);
  }

  const digest = new Uint8Array(32);
  for (let i = 0; i < 4; i++) {
    let lane = state[i];
    for (let byte = 0; byte < 8; byte++) {
      digest[i * 8 + byte] = Number(lane & 0xffn);
      lane >>= 8n;
    }
  }
  return digest;
}

/** UTF-8 encode without pulling in a polyfill — the inputs here are ASCII hex, but be correct anyway. */
export function utf8Bytes(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

export function toHex(bytes: Uint8Array): string {
  let hex = "";
  for (const byte of bytes) hex += byte.toString(16).padStart(2, "0");
  return hex;
}
